import React from 'react';
import { Icon, Button } from './ui';
import ErrorBoundary from './ErrorBoundary';
import '../theme/appShell.css';

/*
 * AppShell — presentational Console shell (variant1): left rail + gradient
 * topbar + content area. It holds NO Redux and NO business logic; App passes
 * everything in as props and this component only renders + fires callbacks.
 *
 * Screen switching reuses the app's EXISTING mechanism: nav clicks call
 * `onSelectScreen(controlKey)` which App maps to the Redux `setAdminControl`
 * action (the same field `AdminScreen` reads to decide which XControl to
 * render). `controlKey === false` means "no admin control" → the live
 * TransactionMonitor (Event Monitor) is the visible screen.
 *
 * Props:
 *   role            : { isAdmin: bool, accountType: 'agency'|'direct', label, gateAclId }
 *   activeControl   : current Redux showAdminControl (string | false) — the
 *                     source of truth for which SCREEN is mounted
 *   activeNav       : presentational nav highlight key (App owns it, so
 *                     Event Monitor vs On-Site vs Display can each stay lit
 *                     even though they all mount the monitor screen)
 *   onSelectScreen  : (controlKey: string|false) => void  — drives setAdminControl
 *   onSelectNav     : (navKey: string) => void            — updates highlight
 *   location        : { customerName, siteName, gateName, subscriberLabel }
 *   user            : { name, roleLabel, initials }
 *   version         : { app, backend }
 *   onSignOut       : () => void   (wired to App.doLogout)
 *   onsiteCount      : number|string (badge on On-Site item; optional)
 *   pageTitle       : string  (topbar title)
 *   children        : the currently-selected screen(s)
 */

const BRAND_SVG = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3Z" fill="var(--brand)" />
    <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11V2Z" fill="#3ea0e0" />
    <path d="M9 12.5 11 15l4-5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Nav model. `control` is the value handed to setAdminControl (false = monitor).
// `nav` is the presentational highlight key. `admin` items only show for admins.
// `orgItem` marks the "Customers & Sites" item so a future accountType==='direct'
// can hide just that item (clean hook, per spec).
const PRIMARY = [
  { nav: 'monitor', control: false, icon: 'dashboard', label: 'Event Monitor' },
  // On-Site — intentionally hidden for now. This will become the live vehicle/person
  // on-site TRACKING screen (roster + per-site occupancy counts). The on-site COUNT
  // already lives on the Event Monitor; the old web app has no distinct on-site screen,
  // so until the tracking screen is built this nav item just duplicated the monitor.
  // Re-enable (and point `control` at the real tracking screen) once it exists.
  // { nav: 'onsite', control: false, icon: 'users', label: 'On-Site', badge: 'onsite' },
];

// The org tree (Subscriber ▸ Customer ▸ Site ▸ Gate) is now the SINGLE provisioning
// surface — ONE "Organization" item. It opens the unified "places tree + context tabs"
// pane where everything is provisioned: customers/sites/gates AND each place's people
// (the old "Users & Guards" now lives in a customer's Users tab) AND guard assignments
// (the old "Gate Assignments" now lives in a gate's Guards tab). No separate items.
const SETUP = [
  { nav: 'customer', control: 'customer', icon: 'tree', label: 'Organization' },
];

// Reference data — not part of standing a gate up; these describe who/what crosses.
const DATA = [
  { nav: 'company', control: 'company', icon: 'company', label: 'Companies' },
  { nav: 'lpn', control: 'lpn', icon: 'plate', label: 'License Plates' },
  { nav: 'person', control: 'person', icon: 'user', label: 'People' },
];

const SETTINGS = [
  // 'display' routes through showAdminControl so AdminScreen can mount the real
  // Display settings screen (theme toggle only). All other behavior
  // (highlight, page title) is unchanged.
  { nav: 'display', control: 'display', icon: 'settings', label: 'Display' },
];

function NavItem({ item, activeNav, onClick, badgeValue }) {
  const isActive = activeNav === item.nav;
  return (
    <button
      type="button"
      className={`nav__item${isActive ? ' active' : ''}`}
      aria-current={isActive ? 'page' : undefined}
      title={item.label}
      onClick={onClick}
    >
      <Icon name={item.icon} />
      <span className="grow truncate">{item.label}</span>
      {item.badge === 'onsite' && badgeValue != null && badgeValue !== '' && badgeValue !== 'N/A' && (
        <span className="nav__badge num">{badgeValue}</span>
      )}
    </button>
  );
}

export default function AppShell({
  role = {},
  activeControl = false,
  activeNav = 'monitor',
  onSelectScreen,
  onSelectNav,
  location = {},
  user = {},
  version = {},
  onSignOut,
  onsiteCount,
  pageTitle,
  children,
}) {
  const isAdmin = !!role.isAdmin;
  const isDirect = role.accountType === 'direct';

  // Rail collapse — icon-only strip for an unobstructed event table on wide monitors.
  // Persisted so the choice survives reloads (localStorage, guarded for private mode).
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem('gw_rail_collapsed') === '1'; } catch (e) { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem('gw_rail_collapsed', next ? '1' : '0'); } catch (e) { /* ignore */ }
    return next;
  });

  const handleNav = (item) => {
    // Re-clicking the setup item that's already open collapses the pane back to the
    // Event Monitor (one of several ways out — see SetupNavHead's caret/title + Done).
    if (item.control && item.control === activeControl) {
      if (onSelectNav) onSelectNav('monitor');
      if (onSelectScreen) onSelectScreen(false);
      return;
    }
    // Paint the nav highlight FIRST (cheap setState), THEN switch the screen (App
    // defers the heavier swap a tick) — so the clicked pill lights up instantly
    // instead of waiting ~2s for the pane's data to load.
    if (onSelectNav) onSelectNav(item.nav);
    if (onSelectScreen) onSelectScreen(item.control);
  };

  const renderGroup = (items) =>
    items
      // hide the org "Customers & Sites" item for direct accounts (clean hook)
      .filter((item) => !(item.orgItem && isDirect))
      .map((item) => (
        <NavItem
          key={item.nav}
          item={item}
          activeNav={activeNav}
          badgeValue={onsiteCount}
          onClick={() => handleNav(item)}
        />
      ));

  const locParts = [];
  if (location.customerName) locParts.push(location.customerName);
  else if (location.subscriberLabel) locParts.push(location.subscriberLabel);
  if (location.siteName) locParts.push(location.siteName);
  if (location.gateName) locParts.push(location.gateName);

  return (
    <div className={`gw-app${collapsed ? ' rail-collapsed' : ''}`}>
      {/* ---- left rail ---- */}
      <aside className="rail">
        <div className="rail__top">
          <div className="brand">{BRAND_SVG}<span>Gate</span><b>Watcher</b></div>
          <button
            type="button"
            className="rail__toggle"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-pressed={collapsed}
          >
            <Icon name={collapsed ? 'chevronRight' : 'chevronLeft'} size={16} />
          </button>
        </div>

        <nav className="nav" aria-label="Primary">
          {renderGroup(PRIMARY)}

          {isAdmin && (
            <>
              <div className="nav__grp"><div className="eyebrow">Setup</div></div>
              {renderGroup(SETUP)}
            </>
          )}

          {/* [86bbumedw] P2 — the Data screens (Companies / License Plates / People) show for ALL
              roles; Guard/Viewer get them READ-ONLY (CataloguePane readOnly). Setup stays admin-only. */}
          <div className="nav__grp"><div className="eyebrow">Data</div></div>
          {renderGroup(DATA)}

          <div className="nav__grp"><div className="eyebrow">Settings</div></div>
          {renderGroup(SETTINGS)}
        </nav>

        {/* user identity + logout live once, top-right in the header (below) — no
            duplicate chip here in the rail foot. */}
        <div className="rail__foot">
          {version.app && (
            <small className="rail__ver">
              v{version.app}{version.backend ? <> &middot; API {version.backend}</> : ''}
            </small>
          )}
        </div>
      </aside>

      {/* ---- main column ---- */}
      <main className="main">
        <header className="topbar">
          <div className="topbar__title">{pageTitle}</div>

          {/* location context breadcrumb (Customer › Site › Gate) */}
          {locParts.length > 0 && (
            <div className="loc" title="Location context">
              <Icon name="location" size={15} />
              {locParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="chev">›</span>}
                  <span className="loc__static">{part}</span>
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="grow" />

          <Clock />

          <div className="userchip" title={version.app ? `App ${version.app}${version.backend ? ' · API ' + version.backend : ''}` : undefined}>
            <div className="avatar">{user.initials || '—'}</div>
            <div className="grow truncate" style={{ maxWidth: 150 }}>
              <strong className="truncate" style={{ display: 'block' }}>{user.name || 'User'}</strong>
              <small>{user.roleLabel || ''}</small>
            </div>
            <Button
              size="sm"
              variant="default"
              className="logout-pill"
              icon={<Icon name="logout" size={15} />}
              onClick={onSignOut}
            >
              Log out
            </Button>
          </div>
        </header>

        {/* content area — the selected screen, guarded so a crash keeps chrome up.
            resetKey = activeControl so navigating away from a crashed screen recovers. */}
        <section className="page">
          <ErrorBoundary resetKey={String(activeNav)}>
            {children}
          </ErrorBoundary>
        </section>
      </main>
    </div>
  );
}

/*
 * Clock — live wall clock in the topbar. Self-contained (its own interval),
 * so App stays free of extra timers. Format mirrors the mock (12h + seconds).
 */
class Clock extends React.Component {
  constructor(props) {
    super(props);
    this.state = { now: new Date() };
  }
  componentDidMount() {
    this.timer = setInterval(() => this.setState({ now: new Date() }), 1000);
  }
  componentWillUnmount() {
    clearInterval(this.timer);
  }
  render() {
    const now = this.state.now;
    const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    return (
      <div className="clock">
        <span className="clock__date">{date}</span>
        <span className="clock__sep" />
        <span className="clock__time">{time}</span>
      </div>
    );
  }
}
